import React, { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import CreatableSelect from 'react-select/creatable';
import { ClientType } from '../models/enums/ClientType';
import { ClientStatus } from '../models/enums/ClientStatus';
import Client from '../models/Client';
import {ContactOption} from "../models/enums/ContactOption";
import {updateClient} from "../services/client-service";

interface EditClientModalProps {
    isOpen: boolean;
    onClose: () => void;
    onError: (message: string) => void;
    onEditClient: (client: Client) => void;
    client: Client;
}

type TagOption = { label: string; value: string };

const EditClientModal: React.FC<EditClientModalProps> = ({
                                                             isOpen,
                                                             onClose,
                                                             onError,
                                                             onEditClient,
                                                             client,
                                                         }) => {
    const typeOptions = Object.values(ClientType).map(t => ({ value: t, label: t }));
    const statusOptions = Object.values(ClientStatus).map(s => ({ value: s, label: s }));
    const preferredContactOptions = Object.values(ContactOption).map(s => ({ value: s, label: s }));
    const maxNotes = 1000;

    const [name, setName] = useState(client.name);
    const [type, setType] = useState<ClientType>(client.type);
    const [status, setStatus] = useState<ClientStatus>(client.status);
    const [phone, setPhone] = useState(client.phone || '');
    const [email, setEmail] = useState(client.email || '');
    const [notes, setNotes] = useState(client.notes || '');
    const[preferredContact, setPreferredContact] = useState(client.preferredContact);
    const [tags, setTags] = useState<TagOption[]>(
        (client.tags || []).map(t => ({ label: t, value: t }))
    );
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setName(client.name);
            setType(client.type);
            setStatus(client.status);
            setPhone(client.phone || '');
            setEmail(client.email || '');
            setNotes(client.notes || '');
            setPreferredContact(client.preferredContact);
            setTags((client.tags || []).map(t => ({ label: t, value: t })));
            setErrors({});
        }
    }, [isOpen, client]);

    const validate = (clientTags: string[]) => {
        const newErrors: Record<string, string> = {};
        if (!name.trim()) newErrors.name = 'Name is required';
        if (name.length > 100) newErrors.name = 'Name must be 100 characters or fewer';
        if (email && !/.+@.+\..+/.test(email)) newErrors.email = 'Invalid email';
        if (email && email.length > 100) newErrors.email = 'Email must be 100 characters or fewer';
        if (phone && !/^\+?[0-9]{7,15}$/.test(phone)) newErrors.phone = 'Invalid phone number (do not use dashes)';

        const totalTagChars = clientTags.reduce((sum, t) => sum + t.length, 0);
        if (totalTagChars > 255) {
            newErrors.tags = `Total tags must be 255 characters or fewer (you have ${totalTagChars})`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        const clientTags = tags.map(t => t.value);

        if (!validate(clientTags)) return;
        setLoading(true);
        const dto: Client = {
            id: client.id,
            name: name,
            type: type,
            status: status,
            phone: phone || undefined,
            email: email || undefined,
            notes: notes || undefined,
            preferredContact: preferredContact || undefined,
            tags: clientTags,
            lastActivityDate: client.lastActivityDate
        };
        try {
            const response = await updateClient(dto);
            if (response.status === 200 && response.data) {
                onEditClient(response.data);
            } else {
                onError('Unable to update client. Please try again.');
            }
        } catch (err) {
            onError('Unable to update client. Please try again.');
        } finally {
            setLoading(false);
            onClose();
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            className="fixed inset-0 z-50 flex items-center justify-center"
        >
            <div
                className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
                aria-hidden="true"
                onClick={onClose}
            />
            <div className="relative bg-white w-full max-w-2xl p-6 rounded-lg shadow-lg overflow-auto max-h-screen">
                <Dialog.Title className="text-xl font-semibold mb-4">
                    Edit Client
                </Dialog.Title>

                {/* Name */}
                <div className="mb-4">
                    <label htmlFor="name" className="block font-medium mb-1">Name</label>
                    <input
                        id="name"
                        type="text"
                        className="w-full border px-3 py-2 rounded"
                        value={name}
                        onChange={e => setName(e.target.value)}
                    />
                    {errors.name && <p data-testid="name-error" className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                {/* Type & Status */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <label htmlFor="type" className="block font-medium mb-1">Type</label>
                        <select
                            id="type"
                            className="w-full border px-3 py-2 rounded"
                            value={type}
                            onChange={e => setType(e.target.value as ClientType)}
                        >
                            {typeOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="status" className="block font-medium mb-1">Status</label>
                        <select
                            id="status"
                            className="w-full border px-3 py-2 rounded"
                            value={status}
                            onChange={e => setStatus(e.target.value as ClientStatus)}
                        >
                            {statusOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <label htmlFor="phone" className="block font-medium mb-1">Phone</label>
                        <input
                            id="phone"
                            type="text"
                            className="w-full border px-3 py-2 rounded"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                        />
                        {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                    </div>
                    <div>
                        <label htmlFor="email" className="block font-medium mb-1">Email</label>
                        <input
                            id="email"
                            type="email"
                            className="w-full border px-3 py-2 rounded"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                        {errors.email && <p data-testid="email-error" className="text-red-500 text-sm mt-1">{errors.email}</p>}
                    </div>
                    <div>
                        <label htmlFor="preferred-contact" className="block font-medium mb-1">Preferred Contact</label>
                        <select
                            id="preferred-contact"
                            className="w-full border px-3 py-2 rounded"
                            value={preferredContact}
                            onChange={e => setPreferredContact(e.target.value as ContactOption)}
                        >
                            {preferredContactOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Notes */}
                <div className="mb-4">
                    <label htmlFor="notes" className="block font-medium mb-1">Notes</label>
                    <textarea
                        id="notes"
                        className="w-full border px-3 py-2 rounded"
                        rows={3}
                        maxLength={maxNotes}
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                    />
                    <p className="text-gray-500 text-sm mt-1">
                        {notes.length}/{maxNotes} characters
                    </p>
                </div>

                {/* Tags */}
                <div data-testid='tags' className="mb-6">
                    <label className="block font-medium mb-1">Tags</label>
                    <CreatableSelect
                        isMulti
                        value={tags}
                        onChange={selected => setTags(selected as TagOption[])}
                        options={tags}
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                    {errors.tags && <p data-testid="tags-error" className="text-red-500 text-sm mt-1">{errors.tags}</p>}
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded border"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 rounded bg-crumbblue text-white"
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </Dialog>
    );
};

export default EditClientModal;